// ============================================
// COSMIC HINDU - Auth Context (Real JWT Auth)
// ============================================

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import authService from '@services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Rehydrate from localStorage so page refresh keeps user logged in
  const [user, setUser] = useState(() => authService.getStoredUser());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // On mount, silently verify stored token is still valid
  useEffect(() => {
    if (authService.isLoggedIn()) {
      authService.getProfile().then((profile) => {
        if (profile) setUser(profile);
        else setUser(null);
      });
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authService.login(email, password);
      if (res.success && res.user) {
        setUser(res.user);
        setIsLoading(false);
        return res;
      }
      throw new Error(res.message || 'Login failed');
    } catch (err) {
      const msg = err.message || 'Invalid email or password';
      setError(msg);
      setIsLoading(false);
      throw msg;
    }
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isLoading,
    error,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
